import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, TrendingUp, Target, BookOpen, Flame, Heart, Plus, CalendarPlus, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [intakeCompleted, setIntakeCompleted] = useState(true);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [bookSessionOpen, setBookSessionOpen] = useState(false);
  const [childData, setChildData] = useState({ name: '', age: '', focus_areas: [] });
  const [bookingData, setBookingData] = useState({
    session_type: 'standard_30',
    date: '',
    time: '',
    notes: ''
  });

  // Redirect admin users to admin portal
  useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    loadStats();
    checkIntake();
  }, []);

  const loadStats = async () => {
    try {
      const response = await API.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkIntake = async () => {
    try {
      const res = await API.get('/intake-form');
      setIntakeCompleted(!!res.data?.form_id);
    } catch { setIntakeCompleted(false); }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    if (submitting) return; // Prevent multiple submissions
    
    setSubmitting(true);
    try {
      await API.post('/children', {
        name: childData.name,
        age: parseInt(childData.age),
        focus_areas: childData.focus_areas.split(',').map(s => s.trim()).filter(Boolean)
      });
      toast.success('Child profile created!');
      setAddChildOpen(false);
      setChildData({ name: '', age: '', focus_areas: [] });
      await loadStats();
    } catch (error) {
      toast.error('Failed to create profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookSession = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    try {
      await API.post('/bookings', {
        coach_id: 'robin_main_coach',  // Robin's coach ID
        ...bookingData
      });
      toast.success('Session request sent! Robin will confirm shortly.');
      setBookSessionOpen(false);
      setBookingData({
        session_type: 'standard_30',
        date: '',
        time: '',
        notes: ''
      });
      await loadStats();
    } catch (error) {
      toast.error('Failed to book session');
    } finally {
      setSubmitting(false);
    }
  };

  const sessionTypes = [
    { value: 'quick_15', label: '15 min Quick Chat' },
    { value: 'standard_30', label: '30 min Standard Session' },
    { value: 'deep_60', label: '60 min Deep Dive' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Current Streak',
      value: stats?.current_streak || 0,
      description: 'days of tracking',
      icon: Flame,
      color: 'from-orange-500 to-red-500',
      link: '/tracker'
    },
    {
      title: 'This Week',
      value: stats?.logs_this_week || 0,
      description: 'logs recorded',
      icon: Calendar,
      color: 'from-blue-500 to-cyan-500',
      link: '/tracker'
    },
    {
      title: 'Active Goals',
      value: stats?.active_goals || 0,
      description: 'in progress',
      icon: Target,
      color: 'from-green-500 to-emerald-500',
      link: '/goals'
    },
    {
      title: 'Upcoming Sessions',
      value: stats?.upcoming_sessions || 0,
      description: 'scheduled',
      icon: BookOpen,
      color: 'from-purple-500 to-pink-500',
      link: '/bookings'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Intake Form Banner */}
      {!intakeCompleted && (
        <div data-testid="intake-banner" className="bg-[#C97B5A]/10 border border-[#C97B5A]/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-[#C97B5A]" />
            <div>
              <p className="font-medium text-[#2c3e50]">Complete Your Intake Form</p>
              <p className="text-sm text-gray-500">Please fill out the intake form so Coach Robin can prepare for your first session.</p>
            </div>
          </div>
          <Button onClick={() => navigate('/intake')} className="bg-[#C97B5A] hover:bg-[#b86a4a]" data-testid="intake-banner-btn">
            Complete Form
          </Button>
        </div>
      )}
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">Welcome back, {user?.name}!</h1>
          <p className="text-muted-foreground">Here's what's happening with your family today</p>
        </div>
        <Dialog open={bookSessionOpen} onOpenChange={setBookSessionOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="bg-gradient-to-r from-violet-600 to-purple-600">
              <CalendarPlus className="w-5 h-5 mr-2" />
              Book Session with Robin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Book a Session with Robin</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBookSession} className="space-y-4">
              <div>
                <Label>Session Type</Label>
                <Select
                  value={bookingData.session_type}
                  onValueChange={(value) => setBookingData({ ...bookingData, session_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date">Preferred Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={bookingData.date}
                  onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="time">Preferred Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={bookingData.time}
                  onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="booking-notes">What would you like to discuss?</Label>
                <Textarea
                  id="booking-notes"
                  placeholder="Any specific topics or concerns..."
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Note: Payment will be handled offline. Robin will confirm your session shortly.
              </p>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Sending Request...' : 'Request Session'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-0 bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-2">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-3xl font-bold">{stat.value}</CardTitle>
                  <CardDescription>{stat.title}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{stat.description}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Children Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Children</CardTitle>
              <CardDescription>Manage profiles and track progress</CardDescription>
            </div>
            <Dialog open={addChildOpen} onOpenChange={setAddChildOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Child
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Child Profile</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddChild} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={childData.name}
                      onChange={(e) => setChildData({ ...childData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="age">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      value={childData.age}
                      onChange={(e) => setChildData({ ...childData, age: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="focus_areas">Focus Areas (comma-separated)</Label>
                    <Input
                      id="focus_areas"
                      placeholder="communication, social skills, sensory"
                      value={childData.focus_areas}
                      onChange={(e) => setChildData({ ...childData, focus_areas: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Profile'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {stats?.children && stats.children.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.children.map((child) => (
                <Card key={child.child_id} className="bg-gradient-to-br from-violet-50 to-purple-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-pink-500" />
                      {child.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/child/${child.child_id}`}>
                      <Button variant="outline" className="w-full">View Dashboard</Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No children added yet</p>
              <Button onClick={() => setAddChildOpen(true)}>Add Your First Child</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/tracker">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <Calendar className="w-10 h-10 text-blue-500 mb-2" />
              <CardTitle>Log Today's Behavior</CardTitle>
              <CardDescription>
                {stats?.today_logged ? 'Update today\'s entry' : 'Haven\'t logged today yet'}
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/reports">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <TrendingUp className="w-10 h-10 text-green-500 mb-2" />
              <CardTitle>View Weekly Report</CardTitle>
              <CardDescription>AI-powered insights and recommendations</CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/community">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <Heart className="w-10 h-10 text-pink-500 mb-2" />
              <CardTitle>Join Community</CardTitle>
              <CardDescription>Connect with other parents</CardDescription>
            </CardHeader>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;